import { useMemo, useCallback } from 'react'
import { DEFAULT_REMOTE_CONFIG, RemoteConfigSettings } from '../../../config'
import { FeatureFlagHook } from '../types'

/**
 * useStaticFeatureFlags
 *
 * Hook for static feature flags (on-premises deployments).
 * Uses DEFAULT_REMOTE_CONFIG with optional runtime overrides.
 */
export default function useStaticFeatureFlags(
  overrides?: Partial<RemoteConfigSettings>
): FeatureFlagHook {
  const flags = useMemo(
    () => ({
      ...DEFAULT_REMOTE_CONFIG,
      ...overrides,
    }),
    [overrides]
  )

  const getFlags = useCallback(() => flags, [flags])

  const isLoaded = useCallback(() => true, [])

  const load = useCallback(async () => {
    // No-op: flags are already loaded from static config
    return Promise.resolve()
  }, [])

  const markAsNotLoaded = useCallback(() => {
    // No-op: static flags don't need reloading
  }, [])

  return useMemo(
    () => ({
      getFlags,
      isLoaded,
      load,
      markAsNotLoaded,
    }),
    [getFlags, isLoaded, load, markAsNotLoaded]
  )
}
